"use client"

import { useState, useEffect, useCallback } from "react"
import { importFile } from "@/lib/api/blogs"
import { getJob } from "@/lib/api/jobs"
import { CommunityEntry, ImportType, isErrorResponse } from "@types"
import { Job, JobEmailImport } from "@/types/jobs"

const MAX_FILE_SIZE = 10000000 // 10MB

interface ModalState {
  showConfirmation: boolean
  showFailed: boolean
  showComplete: boolean
}

interface ImportState {
  file: File | null
  inProgress: boolean
  progress: number
  errors: string[]
  jobId: string
  jobDetails: Job | JobEmailImport | null
}

interface SegmentState {
  selected: CommunityEntry | null
  intoCommunities: boolean
  communityIds: string[]
}

export function useImportSubscribersLogic() {
  // Grouped related state to reduce the number of individual useState calls
  const [importState, setImportState] = useState<ImportState>({
    file: null,
    inProgress: false,
    progress: 0,
    errors: [],
    jobId: "",
    jobDetails: null,
  })

  const [modalState, setModalState] = useState<ModalState>({
    showConfirmation: false,
    showFailed: false,
    showComplete: false,
  })

  const [segmentState, setSegmentState] = useState<SegmentState>({
    selected: null,
    intoCommunities: false,
    communityIds: [],
  })

  // Handle file selection
  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) {
        setImportState((prev) => ({ ...prev, file }))
      }
    },
    []
  )

  // Handle import button click
  const handleImportClick = useCallback(() => {
    if (!importState.file) return

    // If the file is too large, show an error
    if (importState.file.size > MAX_FILE_SIZE) {
      setImportState((prev) => ({
        ...prev,
        errors: ["File is too large. Please upload a file smaller than 10 MB."],
      }))
      setModalState((prev) => ({ ...prev, showFailed: true }))
      return
    }

    // If we're importing into communities but no community is selected
    if (
      segmentState.intoCommunities &&
      segmentState.communityIds.length === 0
    ) {
      setImportState((prev) => ({
        ...prev,
        errors: ["Please select at least one community to import into."],
      }))
      setModalState((prev) => ({ ...prev, showFailed: true }))
      return
    }

    // Show confirmation modal
    setModalState((prev) => ({ ...prev, showConfirmation: true }))
  }, [
    importState.file,
    segmentState.intoCommunities,
    segmentState.communityIds,
  ])

  // Handle confirmed import
  const handleConfirmedImport = useCallback(async () => {
    if (!importState.file) return

    setModalState((prev) => ({
      ...prev,
      showConfirmation: false,
    }))
    setImportState((prev) => ({ ...prev, inProgress: true, progress: 0 }))

    try {
      const result = await importFile(
        importState.file,
        "free" as ImportType,
        segmentState.intoCommunities,
        segmentState.communityIds
      )

      if (isErrorResponse(result)) {
        console.error("Error importing file:", result)
        setImportState((prev) => ({
          ...prev,
          inProgress: false,
          errors: [result.msg || "Error importing file"],
        }))
        setModalState((prev) => ({
          ...prev,
          showFailed: true,
        }))
        return
      }

      setImportState((prev) => ({ ...prev, jobId: result.jobId }))
    } catch (error) {
      console.error("Error importing file:", error)
      setImportState((prev) => ({
        ...prev,
        inProgress: false,
        errors: ["Something went wrong importing the file."],
      }))
      setModalState((prev) => ({
        ...prev,
        showFailed: true,
      }))
    }
  }, [
    importState.file,
    segmentState.intoCommunities,
    segmentState.communityIds,
  ])

  // Handle segment selection
  const handleSegmentSelect = useCallback((segment: CommunityEntry | null) => {
    if (segment) {
      setSegmentState({
        selected: segment,
        intoCommunities: true,
        communityIds: [segment.id],
      })
    } else {
      setSegmentState({
        selected: null,
        intoCommunities: false,
        communityIds: [],
      })
    }
  }, [])

  // Handle try again after failure
  const handleTryAgain = useCallback(
    (fileInputRef: React.RefObject<HTMLInputElement>) => {
      setModalState((prev) => ({ ...prev, showFailed: false }))
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
      setImportState((prev) => ({ ...prev, file: null }))
    },
    []
  )

  // Utility function for sleeping
  const sleep = useCallback(
    (ms: number) => new Promise((resolve) => setTimeout(resolve, ms)),
    []
  )

  // Check job status
  useEffect(() => {
    if (!importState.jobId) return

    let cancelJob = false

    const refreshImportJobProgressAsync = async () => {
      let count = 1
      let sleepTime = 1000

      while (count < 100000 && !cancelJob) {
        console.log("About to refresh job details.")
        count++
        await sleep(sleepTime)

        // Slow down polling rate for long-running jobs
        if (count > 25) sleepTime = 2000
        else if (count > 50) sleepTime = 4000
        else if (count > 75) sleepTime = 6000

        try {
          const job = await getJob(importState.jobId)

          if (isErrorResponse(job)) {
            console.error("Error getting job:", job)
            continue
          }

          setImportState((prev) => ({
            ...prev,
            jobDetails: job,
            progress: job.percentCompletion || prev.progress,
          }))

          // Handle both complete success and partial success
          if (
            job.status === "FINISHED" ||
            (job.status === "ERROR" && job.errorCode === "PARTIAL_SUCCESS")
          ) {
            console.log(
              "Import job finished",
              job.status === "FINISHED"
                ? "successfully"
                : "with partial success"
            )

            setImportState((prev) => ({
              ...prev,
              inProgress: false,
              progress: 100, // Force progress to 100% for UI consistency
            }))

            setModalState((prev) => ({
              ...prev,
              showComplete: true,
            }))

            return
          }

          // Handle complete failure
          if (job.status === "ERROR" && job.errorCode !== "PARTIAL_SUCCESS") {
            console.log("Import job failed:", job.errorMessagePublic)

            setImportState((prev) => ({
              ...prev,
              inProgress: false,
              errors: [job.errorMessagePublic || "Import failed"],
            }))

            setModalState((prev) => ({
              ...prev,
              showFailed: true,
            }))

            return
          }
        } catch (error) {
          console.error("Error fetching job status:", error)
        }
      }
    }

    refreshImportJobProgressAsync()

    return () => {
      cancelJob = true
    }
  }, [importState.jobId, sleep])

  // Create an interface that looks similar to the original for easier migration
  return {
    // Import state properties
    selectedFile: importState.file,
    jobId: importState.jobId,
    importInProgress: importState.inProgress,
    importProgress: importState.progress,
    importErrors: importState.errors,
    importJobDetails: importState.jobDetails,

    // Segment state properties
    selectedSegment: segmentState.selected,
    importIntoCommunities: segmentState.intoCommunities,
    selectedCommunityIds: segmentState.communityIds,

    // Modal state properties
    showConfirmationModal: modalState.showConfirmation,
    setShowConfirmationModal: (show: boolean) =>
      setModalState((prev) => ({ ...prev, showConfirmation: show })),

    showFailedModal: modalState.showFailed,
    setShowFailedModal: (show: boolean) =>
      setModalState((prev) => ({ ...prev, showFailed: show })),

    showCompleteModal: modalState.showComplete,
    setShowCompleteModal: (show: boolean) =>
      setModalState((prev) => ({ ...prev, showComplete: show })),

    // Handlers
    handleFileChange,
    handleImportClick,
    handleConfirmedImport,
    handleSegmentSelect,
    handleTryAgain,
  }
}
